import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGraduationCap,
  FaBookOpen,
  FaChartLine,
  FaAward,
  FaSignOutAlt,
  FaArrowRight,
  FaPlay,
  FaFlask,
  FaCalculator,
  FaLaptopCode,
  FaLanguage,
  FaPalette,
  FaBell,
  FaBullhorn,
  FaCog,
  FaCommentDots,
  FaInfoCircle,
  FaMoon,
  FaQuestionCircle,
  FaSearch,
  FaSun,
  FaUserEdit,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import UniversityFooter from '../../components/UniversityFooter/UniversityFooter';
import { useNotifications } from '../../context/NotificationContext';
import Loader from '../../components/Loader/Loader';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import './UniversityDashboard.css';

const categoryIcons = {
  Science: <FaFlask />,
  Commerce: <FaCalculator />,
  Technology: <FaLaptopCode />,
  Languages: <FaLanguage />,
  'Arts & Humanities': <FaPalette />,
};

const UniversityDashboard = () => {
  const navigate = useNavigate();
  const { token, profile, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentExams, setRecentExams] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [navbarSearch, setNavbarSearch] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(() => localStorage.getItem('uniTheme') === 'dark');
  const profileDropdownRef = useRef(null);
  const { notificationCount } = useNotifications();

  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    try {
      const [catRes, analyticsRes, historyRes, subjectRes] = await Promise.all([
        axios.get(api.getExamCategories, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${api.baseURL}/api/exams/analytics/performance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${api.getExamHistory}?limit=5`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(api.getUserSubjectSelection, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (catRes.data.success) setCategories(catRes.data.data);
      if (analyticsRes.data.success) setAnalytics(analyticsRes.data.data);
      if (historyRes.data.success) {
        const attemptedExams = (historyRes.data.data.exams || []).filter(
          (exam) => exam.status !== 'created'
        );
        setRecentExams(attemptedExams);
      }
      if (subjectRes.data.success) setSelectedSubjects(subjectRes.data.data.subjects || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    localStorage.setItem('uniTheme', isDarkTheme ? 'dark' : 'light');
  }, [isDarkTheme]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const searchResults = useMemo(() => {
    const query = navbarSearch.trim().toLowerCase();
    if (!query) return [];

    const items = [];

    Object.entries(categories || {}).forEach(([categoryName, subcategories]) => {
      items.push({
        type: 'Category',
        label: categoryName,
        description: 'Explore this category',
        path: '/university/subjects',
        state: { selectedCategory: categoryName, searchQuery: categoryName },
      });

      Object.entries(subcategories || {}).forEach(([subcategoryName, topics]) => {
        items.push({
          type: 'Subcategory',
          label: subcategoryName,
          description: categoryName,
          path: '/university/subjects',
          state: { selectedCategory: categoryName, searchQuery: subcategoryName },
        });

        (topics || []).forEach((topic) => {
          items.push({
            type: 'Subject',
            label: topic,
            description: `${categoryName} / ${subcategoryName}`,
            path: '/university/subjects',
            state: { selectedCategory: categoryName, searchQuery: topic },
          });
        });
      });
    });

    recentExams.forEach((exam) => {
      items.push({
        type: 'Exam',
        label: exam.subject,
        description: `${exam.level || 'Exam'} result`,
        path: `/university/result/${exam._id}`,
      });
    });

    return items
      .filter((item) =>
        `${item.label} ${item.description} ${item.type}`.toLowerCase().includes(query),
      )
      .slice(0, 6);
  }, [categories, navbarSearch, recentExams]);

  if (loading) return <Loader message="Loading University Dashboard..." />;

  const displayName = profile?.name || 'Scholar';
  const mailId = profile?.email || profile?.mailID || profile?.mailId || 'No email available';
  const profileImage = profile?.profilePicture
    ? getProfilePictureUrl(profile.profilePicture, api.img)
    : '';
  const profileInitial = displayName.trim().charAt(0).toUpperCase() || 'S';

  const renderAvatar = (className) => (
    profileImage ? (
      <img src={profileImage} alt="profile" className={className} />
    ) : (
      <span className={`${className} uni-navbar-profile-initial`}>
        {profileInitial}
      </span>
    )
  );

  const handleDropdownNavigate = (path, options) => {
    setShowProfileDropdown(false);
    navigate(path, options);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = navbarSearch.trim();
    if (!query) return;
    navigate('/university/subjects', { state: { searchQuery: query } });
  };

  const handleSearchResultClick = (result) => {
    setNavbarSearch('');
    navigate(result.path, result.state ? { state: result.state } : undefined);
  };

  const formatRecentBattleDate = (dateValue) =>
    new Date(dateValue).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });

  return (
    <div className={`uni-dashboard ${isDarkTheme ? 'uni-dashboard-dark' : ''}`}>
      {/* Header */}
      <header className="uni-dashboard-header">
        <div className="uni-dashboard-header-left">
          <FaGraduationCap className="uni-dashboard-logo-icon" />
          <h1>ULSAAPP University</h1>
        </div>
          <form className="uni-navbar-search" onSubmit={handleSearchSubmit}>
            <FaSearch className="uni-navbar-search-icon" />
            <input
              type="text"
              value={navbarSearch}
              onChange={(event) => setNavbarSearch(event.target.value)}
              placeholder="Search subjects or exams"
              aria-label="Search subjects or exams"
            />
            {navbarSearch.trim() && (
              <div className="uni-navbar-search-results">
                {searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <button
                      key={`${result.type}-${result.label}-${result.description}`}
                      type="button"
                      className="uni-navbar-search-result"
                      onClick={() => handleSearchResultClick(result)}
                    >
                      <span>{result.label}</span>
                      <small>{result.type} - {result.description}</small>
                    </button>
                  ))
                ) : (
                  <div className="uni-navbar-search-empty">No matching subjects or exams</div>
                )}
              </div>
            )}
          </form>
          <div className="uni-dashboard-header-right">
          <button
            type="button"
            className="uni-navbar-help-btn"
            onClick={() => navigate('/university/guide')}
          >
            <FaQuestionCircle /> Help/Guide
          </button>
          <button
            type="button"
            className="uni-navbar-theme-toggle"
            onClick={() => setIsDarkTheme((prev) => !prev)}
            aria-label={`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`}
          >
            {isDarkTheme ? <FaSun /> : <FaMoon />}
          </button>
          <div className="uni-academy-btn-wrapper">
            <button className="uni-dashboard-academy-btn" onClick={() => navigate('/home')}>
              <FaBookOpen /> Prepare with Academy
            </button>
            <div className="uni-academy-notice">
              <FaBookOpen />
              <p>
                <strong>Academy is optional.</strong> Learning happens anywhere — YouTube, courses, books.
                The University evaluates you through exams. Use Academy only if you need preparation material.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="uni-navbar-notification-btn"
            onClick={() => navigate('/notifications')}
            aria-label="Open notifications"
          >
            <FaBell />
            {notificationCount > 0 && (
              <span className="uni-navbar-notification-badge">
                {notificationCount}
              </span>
            )}
          </button>
          <div className="uni-navbar-profile-wrapper" ref={profileDropdownRef}>
            <button
              type="button"
              className="uni-navbar-profile-btn"
              onClick={() => setShowProfileDropdown((prev) => !prev)}
              aria-expanded={showProfileDropdown}
              aria-label="Open profile menu"
            >
              {renderAvatar('uni-navbar-profile-avatar')}
            </button>

            {showProfileDropdown && (
              <div className="uni-navbar-profile-dropdown">
                <div className="uni-navbar-dropdown-user">
                  {renderAvatar('uni-navbar-dropdown-avatar')}
                  <div>
                    <p className="uni-navbar-dropdown-name">{displayName}</p>
                    <p className="uni-navbar-dropdown-mail">{mailId}</p>
                  </div>
                </div>

                <p className="uni-navbar-dropdown-section-label">Navigation</p>
                <button className="uni-navbar-dropdown-item" onClick={() => handleDropdownNavigate('/edit-profile')}>
                  <FaUserEdit /> Edit Profile
                </button>
                <button className="uni-navbar-dropdown-item" onClick={() => handleDropdownNavigate('/university/my-subjects')}>
                  <FaBookOpen /> My Subjects
                </button>
                <button className="uni-navbar-dropdown-item" onClick={() => handleDropdownNavigate('/notifications')}>
                  <FaBell /> Notifications
                </button>
                <button
                  className="uni-navbar-dropdown-item"
                  onClick={() => handleDropdownNavigate('/notifications', { state: { filter: 'university-alerts' } })}
                >
                  <FaBullhorn /> Announcements
                </button>
                <button className="uni-navbar-dropdown-item" onClick={() => handleDropdownNavigate('/users-feedback')}>
                  <FaCommentDots /> Feedback
                </button>
                <button className="uni-navbar-dropdown-item" onClick={() => handleDropdownNavigate('/account-settings')}>
                  <FaCog /> Account Settings
                </button>
                <button className="uni-navbar-dropdown-item" onClick={() => handleDropdownNavigate('/about')}>
                  <FaInfoCircle /> About Us
                </button>
                <button className="uni-navbar-dropdown-logout" onClick={handleLogout}>
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="uni-dashboard-layout">
        <aside className="uni-dashboard-sidebar">
          <p className="uni-sidebar-label">Dashboard Sections</p>
          <button className="uni-sidebar-stat-btn" onClick={() => navigate('/university/my-subjects')}>
            <FaGraduationCap />
            <span>
              My Subjects
              <small>{selectedSubjects.length}</small>
            </span>
          </button>
          <button className="uni-sidebar-stat-btn" onClick={() => navigate('/university/exam-history')}>
            <FaBookOpen />
            <span>
              Exams Conquered
              <small>{analytics?.totalExamsTaken || 0}</small>
            </span>
          </button>
          <button className="uni-sidebar-stat-btn" onClick={() => navigate('/university/analytics')}>
            <FaChartLine />
            <span>
              Accuracy Rate
              <small>{(analytics?.overallAccuracy || 0).toFixed(1)}%</small>
            </span>
          </button>
          <button className="uni-sidebar-stat-btn" onClick={() => navigate('/university/certificates')}>
            <FaAward />
            <span>
              Certificates
              <small>{analytics?.certificatesEarned || 0}</small>
            </span>
          </button>
          <button className="uni-sidebar-stat-btn" onClick={() => navigate('/university/guide')}>
            <FaQuestionCircle />
            <span>
              Help / Guide
              <small>?</small>
            </span>
          </button>
        </aside>

        <main className="uni-dashboard-main">
          {/* Quick Stats — Engaging stat labels */}
          {false && (
          <section className="uni-dashboard-stats">
            <div
              className="uni-stat-card clickable"
              onClick={() => navigate('/university/my-subjects')}
            >
              <FaGraduationCap className="uni-stat-icon" />
              <div className="uni-stat-info">
                <span className="uni-stat-number">{selectedSubjects.length}</span>
                <span className="uni-stat-label">My Subjects</span>
              </div>
            </div>
            <div className="uni-stat-card clickable" onClick={() => navigate('/university/exam-history')}>
              <FaBookOpen className="uni-stat-icon" />
              <div className="uni-stat-info">
                <span className="uni-stat-number">{analytics?.totalExamsTaken || 0}</span>
                {/* Total exams completed */}
                <span className="uni-stat-label">Exams Conquered</span>
              </div>
            </div>
            <div className="uni-stat-card clickable" onClick={() => navigate('/university/analytics')}>
              <FaChartLine className="uni-stat-icon" />
              <div className="uni-stat-info">
                <span className="uni-stat-number">{(analytics?.overallAccuracy || 0).toFixed(1)}%</span>
                {/* Overall accuracy percentage */}
                <span className="uni-stat-label">Accuracy Rate</span>
              </div>
            </div>
            <div className="uni-stat-card clickable" onClick={() => navigate('/university/certificates')}>
              <FaAward className="uni-stat-icon" />
              <div className="uni-stat-info">
                <span className="uni-stat-number">{analytics?.certificatesEarned || 0}</span>
                <span className="uni-stat-label">Certificates</span>
              </div>
            </div>
          </section>
          )}

      {/* Quick Actions — Primary navigation shortcuts */}
      <section className="uni-dashboard-actions">
        {/* Catchy call-to-action heading */}
        <h2>Jump Right In</h2>
        <div className="uni-action-grid">
          <button className="uni-action-card" onClick={() => navigate('/university/subjects')}>
            <FaBookOpen />
            <span>Select Subjects</span>
          </button>
          <button className="uni-action-card uni-action-primary" onClick={() => navigate('/university/exam')}>
            <FaPlay />
            <span>Start Exam</span>
          </button>


          {/* <button className="uni-action-card" onClick={() => navigate('/university/analytics')}>
            <FaChartLine />
            <span>My Insights</span>
          </button>
          <button className="uni-action-card" onClick={() => navigate('/university/certificates')}>
            <FaAward />
            <span>My Trophies</span>
          </button> */}
        
          
        </div>
      </section>

      {/* Subject Categories — Browse all available exam subjects */}
      <section className="uni-dashboard-categories">
        <div className="uni-section-header">
          {/* Catchy heading for subject browsing */}
          <h2>Discover & Explore</h2>
          <button className="uni-view-all-btn" onClick={() => navigate('/university/subjects')}>
            View All <FaArrowRight />
          </button>
        </div>
        <div className="uni-category-grid">
          {categories && Object.entries(categories).map(([catName, subcats]) => (
            <div
              key={catName}
              className="uni-category-card"
              onClick={() => navigate('/university/subjects', { state: { selectedCategory: catName } })}
            >
              <div className="uni-category-icon">
                {categoryIcons[catName] || <FaBookOpen />}
              </div>
              <h3>{catName}</h3>
              <p>{Object.keys(subcats).length} subcategories</p>
              <span className="uni-category-subjects">
                {Object.keys(subcats).slice(0, 3).join(', ')}
                {Object.keys(subcats).length > 3 ? '...' : ''}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Recent Exams — Latest exam attempts */}
      {recentExams.length > 0 && (
        <section className="uni-dashboard-recent">
          <div className="uni-section-header">
            {/* Gamified heading for exam history */}
            <h2>Your Recent Battles</h2>
            <button className="uni-view-all-btn" onClick={() => navigate('/university/exam-history')}>
              View All <FaArrowRight />
            </button>
          </div>
          <div className="uni-recent-list">
            {recentExams.map((exam) => (
              <div
                key={exam._id}
                className="uni-recent-item"
                onClick={() => navigate(`/university/result/${exam._id}`)}
              >
                <div className="uni-recent-info">
                  <h4>{exam.subject}</h4>
                  <span className={`uni-level-badge uni-diff-${exam.level}`}>
                    {exam.level}
                  </span>
                </div>
                <div className="uni-recent-score">
                  <span className={`uni-score ${exam.percentage >= 45 ? 'uni-pass' : 'uni-fail'}`}>
                    {exam.percentage?.toFixed(0)}%
                  </span>
                  <span className="uni-recent-date">
                    {formatRecentBattleDate(exam.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Performance Overview — Subject-wise progress tracking */}
      {analytics?.subjectPerformance?.length > 0 && (
        <section className="uni-dashboard-performance">
          <div className="uni-section-header">
            {/* Dynamic heading for performance section */}
            <h2>Your Progress Pulse</h2>
            <button className="uni-view-all-btn" onClick={() => navigate('/university/analytics')}>
              {/* Catchy CTA for detailed analytics */}
              Deep Dive <FaArrowRight />
            </button>
          </div>
          <div className="uni-performance-grid">
            {analytics.subjectPerformance.slice(0, 4).map((sp, index) => (
              <div key={index} className="uni-performance-card">
                <h4>{sp.subject}</h4>
                <div className="uni-progress-bar-container">
                  <div
                    className="uni-progress-bar"
                    style={{ width: `${sp.averageScore}%` }}
                  ></div>
                </div>
                <div className="uni-performance-stats">
                  <span>Avg: {sp.averageScore.toFixed(1)}%</span>
                  <span>Best: {sp.highestScore.toFixed(1)}%</span>
                  <span>{sp.totalAttempts} attempts</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
        </main>
      </div>

      <UniversityFooter />
    </div>
  );
};

export default UniversityDashboard;
